import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../containers/ApiContainer";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { ActivityFilters } from "./ActivityFilters";
import { WeeklyActivity } from "./WeeklyActivity";
import { RecentActivity } from "./RecentActivity";
import { FBOActivity } from "./FBOActivity";

export const Activity = () => {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [metrics, setMetrics] = React.useState<any>(null);
  const [recentActivity, setRecentActivity] = React.useState<any[]>([]);
  const [weeklyGrid, setWeeklyGrid] = React.useState<any>(null);
  const [showTestFBOs, setShowTestFBOs] = React.useState(false);
  const [filtersDialog, setFiltersDialog] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(0); // 0: Weekly, 1: Recent, 2: FBO

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load all data in parallel
      const [metricsRes, activityRes, gridRes] = await Promise.all([
        postgrest.rpc("api_get_activity_metrics"),
        postgrest.rpc("api_get_recent_activity", { p_limit: 50 }),
        postgrest.rpc("api_get_weekly_activity_grid", { p_weeks: 52 }),
      ]);

      if ((metricsRes.data as any)?.success) {
        setMetrics((metricsRes.data as any).metrics);
      }

      if ((activityRes.data as any)?.success) {
        setRecentActivity((activityRes.data as any).activity || []);
      }

      if ((gridRes.data as any)?.success) {
        setWeeklyGrid((gridRes.data as any).data);
      }
    } catch (err) {
      setError(t("screens.activity.errors.loadFailed"));
      console.error("Error loading activity data:", err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // For FBO Activity tab - use the useActiveFBOs hook
  const { fbos } = useActiveFBOs();

  const exportReport = async () => {
    try {
      const res = await postgrest.rpc("api_export_activity_report");
      if ((res.data as any)?.success) {
        // Convert to CSV or download JSON
        const blob = new Blob(
          [JSON.stringify((res.data as any).report, null, 2)],
          {
            type: "application/json",
          }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `activity-report-${
          new Date().toISOString().split("T")[0]
        }.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Error exporting report:", err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        overflowY: "auto",
        // Remove overflowX: hidden to allow horizontal scrolling within child components
        "&::-webkit-scrollbar": {
          width: "14px",
          height: "14px",
        },
        "&::-webkit-scrollbar-track": {
          background: (theme) => theme.palette.action.hover,
          borderRadius: "7px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: (theme) => theme.palette.text.secondary,
          borderRadius: "7px",
          border: (theme) => `2px solid ${theme.palette.action.hover}`,
          "&:hover": {
            background: (theme) => theme.palette.text.primary,
          },
        },
        "&::-webkit-scrollbar-corner": {
          background: (theme) => theme.palette.action.hover,
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          width: "100%",
          maxWidth: 1400,
          mx: "auto",
          p: 2,
          pb: 15, // Add substantial bottom padding to ensure full visibility
        }}
      >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">{t("screens.activity.title")}</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setFiltersDialog(true)}
          >
            {t("screens.activity.manageFiltersButton")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
          >
            {t("screens.activity.refreshButton")}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportReport}
          >
            {t("screens.activity.exportReportButton")}
          </Button>
        </Box>
      </Box>

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label={t("screens.activity.ariaLabel")}>
          <Tab label={t("screens.activity.tabs.weeklyActivity")} />
          <Tab label={t("screens.activity.tabs.recentActivity")} />
          <Tab label={t("screens.activity.tabs.fboActivity")} />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <WeeklyActivity
          metrics={metrics}
          weeklyGrid={weeklyGrid}
          showTestFBOs={showTestFBOs}
          onShowTestFBOsChange={setShowTestFBOs}
        />
      )}

      {activeTab === 1 && (
        <RecentActivity recentActivity={recentActivity} />
      )}

      {activeTab === 2 && (
        <FBOActivity fbos={fbos} />
      )}

      {/* Filters Dialog */}
      <Dialog
        open={filtersDialog}
        onClose={() => setFiltersDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">{t("screens.activity.dialog.title")}</Typography>
            <IconButton onClick={() => setFiltersDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <ActivityFilters />
        </DialogContent>
      </Dialog>
    </Stack>
    </Box>
  );
};
